import Layout from '@components/layout/Main'
import Background from '@components/image/Background'
import TruckSearchpageForm from '@components/search/forms/TruckSearchpageForm'

import ViewContextProvider, { VIEWS } from '@context/ViewContext'
import BreadcrumbBar from '@components/breadcrumbs/BreadcrumbBar'
import Sort from '@components/select/Sort'
import State from '@components/select/State'
import View from '@components/select/View'
import { TRUCK_LINK_BASE } from '@constants/searchFormLinks'

import MakeModelLinks from '@components/links/MakeModelLinks'
import AdList from '@components/lists/AdList'
import FAQ from '@components/shared/FAQ'
import FrontpageLinks from '@components/frontpage/FrontpageLinks'
import SEARCH_TRUCK_LINKS from '@components/frontpage/constants/searchTruckLinks'

import SeoHeadings from '@pages/search/SearchSeoHeadings'
import Seo from 'src/seo/components/Seo'
import JsonLdSearch from 'src/seo/jsonld/JsonLdSearch'

import BlogSample from '@components/blog/components/BlogSample'

import dbConnect from 'db/dbConnect'
import listTruckAds from 'db/repo/listAds/listAdsTruck'
import sessionCookies from '@utils/sessionCookies'
import useUrlSeo from 'src/seo/server/useUrlSeo'
import useSeoArticle from 'src/seo/components/articles/useSeoArticle'
import substituteSearchQuery from '@pages/search/functions/substituteSearchQuery'
import locationLdSchema from 'src/seo/jsonld/server/locationLdSchema'
import unslugify from '@utils/links/searchUnslugifyNumericParams'
import getMakeModelLinks from '@pages/search/server/getMakeModelLinks'
import makeSearchTruckLinks from '@components/frontpage/functions/makeSearchTruckLinks'
// import cutCanonical from 'src/seo/server/cutCanonical'
import appendAdvancedSearchText from 'src/seo/server/appendAdvancedSearchText'
import truckFallbackSeo from 'src/seo/server/fallbackSeoTruck'
import blogSample from 'db/repo/blogSample'
import getRidOfCategoryLinkForConsecutivePages from '@components/frontpage/functions/getRidOfCategoryLinkForConsecutivePages'
import readSeoArticleFile from 'src/seo/server/readSeoArticleFile'
import redirectFromDostawczakiLegacyUrl from 'src/seo/server/redirectFromDostawczakiLegacyUrl'

const DEFAULT_HEADINGS = {
    h1: 'Samochody dostawcze',
    h2: 'Auta dostawcze najnowsze oferty sprzedaży zobacz w kategorii samochody dostawcze - dostawczaki.pl',
}

export default function TruckSearchpage({
    articleSample,
    cubeLinks,
    footerArticle,
    locationObjectLd,
    makeModelLinks,
    offers,
    pagination,
    query,
    seo,
}) {

    const [seoArticleExcerpt, seoArticleBody] = useSeoArticle(
        seo.articleFile,
        seo.articleFileImage,
        `${query.marka || ''} ${query.model || ''} - samochody dostawcze - dostawczaki.pl`,
        Boolean(seo.headText),
    )

    const queryParamCount = Object.keys(query || {}).length
    const makeModelLinkProps = {
        links: makeModelLinks,
        linkBase: TRUCK_LINK_BASE,
        make: query.marka,
        prefix: 'Dostawcze',
        show: Boolean(queryParamCount === 0 || queryParamCount === 1 && query.marka),
        titles: ['Samochody dostawcze marki', 'Dostawcze modele']
    }

    return (
        <Layout footerArticle={footerArticle}>
            <Seo seo={seo} offers={offers} />
            <JsonLdSearch
                baseUrl="/samochody-dostawcze"
                buy={false}
                category="Samochody dostawcze"
                locationObjectLd={locationObjectLd}
                offers={offers} />

            <Background
                collapseWhen="form_expands"
                height={320}
                src="/backgrounds/samochody-dostawcze-uzywane-i-nowe-na-sprzedaz.webp"
                alt="Samochody dostawcze nowe i używane"
                z={7}>

                <TruckSearchpageForm query={query} />

            </Background>

            <ViewContextProvider defaultView={VIEWS.LIST}>
                <SeoHeadings seo={seo} defaultValues={DEFAULT_HEADINGS} />
                {seoArticleExcerpt}

                <BreadcrumbBar>
                    <State />
                    <Sort />
                    <View />
                </BreadcrumbBar>

                <AdList {...{
                    above: <MakeModelLinks {...makeModelLinkProps} viewOnMobile={false} />,
                    below: <>
                        {seoArticleBody}
                        <MakeModelLinks {...makeModelLinkProps} />
                    </>,
                    offers,
                    pagination,
                }} />

            </ViewContextProvider>

            <BlogSample articles={articleSample} />
            <FrontpageLinks sections={cubeLinks} />
            <FAQ qa={seo.faqFile} page="truck_searchpage" />
        </Layout>
    )
}

export async function getServerSideProps(context) {
    await dbConnect()

    const redirect = await redirectFromDostawczakiLegacyUrl({
        adType: 'truck_ad',
        baseUrl: '/samochody-dostawcze',
        idSlug: context.query.marka,
        prefixId: 'dostawczaki_pl_',
    })

    if (redirect) return redirect

    const query = unslugify(context.query)
    const cookies = sessionCookies(context)
    const { ads, pagination } = await listTruckAds(query, { cookies })
    const articleSample = await blogSample(4, query, {
        tags: [
            'Dostawcze',
            'Dostawczy',
            'Ładowność auta dostawczego',
            'Samochody ciężarowe',
            'Samochody dostawcze',
            'Samochody dostawcze skrzyniowe',
            'Samochody dostawcze typu chłodnie',
            'Samochody dostawcze typu furgon',
            'Samochody dostawcze typu kontener',
            'Samochody dostawcze typu wywrotka',
            'dostawcze', 'dostawczy', 'samochody dostawcze',
        ],
    }, false)

    const cubeLinks = getRidOfCategoryLinkForConsecutivePages(
        await makeSearchTruckLinks(query, '/samochody-dostawcze', 'Dostawcze', SEARCH_TRUCK_LINKS),
        query,
    )

    return {
        props: {
            articleSample,
            cubeLinks,
            footerArticle: await readSeoArticleFile('footer-dostawcze', Object.keys(query).length === 0),
            query,
            offers: ads,
            pagination,
            seo: // cutCanonical(context, 'truck_ad')(
                appendAdvancedSearchText(await useUrlSeo({
                    resolvedUrl: context.resolvedUrl,
                    query,
                    baseUrl: '/samochody-dostawcze',
                    substituteQuery: substituteSearchQuery(query),
                    defaultSeo: {
                        ...DEFAULT_HEADINGS,
                        title: DEFAULT_HEADINGS.h1,
                        description: DEFAULT_HEADINGS.h2,
                    },
                    fallbackFunction: truckFallbackSeo,
                }), query),
            // ),
            locationObjectLd: locationLdSchema(query.lokalizacja),
            makeModelLinks: await getMakeModelLinks('truck_ad', query),
        }
    }
}